import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Wallet, ArrowsClockwise, X } from "@phosphor-icons/react";

const rupiah = (n) => "Rp" + (n || 0).toLocaleString("id-ID");
const STATUSES = ["", "pending", "paid", "expired", "failed", "cancelled", "refunded"];
const TONE = {
  paid: "bg-emerald-100 text-emerald-700", pending: "bg-amber-100 text-amber-700",
  refunded: "bg-rose-100 text-rose-600", failed: "bg-rose-100 text-rose-600",
  expired: "bg-slate-100 text-slate-500", cancelled: "bg-slate-100 text-slate-500",
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" }) : "—");

export default function DigitalTransactions() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [detail, setDetail] = useState(null);
  const [syncing, setSyncing] = useState("");
  const PAGE = 20;

  const load = useCallback(() => {
    const p = new URLSearchParams({ page, page_size: PAGE });
    if (status) p.set("status", status);
    if (start) p.set("start", start);
    if (end) p.set("end", end);
    api.get(`/admin/relax/digital-orders?${p.toString()}`)
      .then((r) => { setRows(r.data.orders); setTotal(r.data.total); })
      .catch((e) => toast.error(apiError(e)));
  }, [page, status, start, end]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (o) => {
    try { setDetail((await api.get(`/admin/relax/digital-orders/${o.order_no}`)).data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const sync = async (order_no) => {
    setSyncing(order_no);
    try { const r = await api.post(`/admin/relax/digital-orders/${order_no}/sync`); toast.success(`Status: ${r.data.status}`); load(); if (detail?.order_no === order_no) openDetail({ order_no }); }
    catch (e) { toast.error(apiError(e)); } finally { setSyncing(""); }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div className="space-y-5" data-testid="digital-transactions">
      <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold text-[#4C1D95]"><Wallet size={24} weight="duotone" className="text-[#DB2777]" /> Transaksi Digital</h1>
      <p className="-mt-3 text-sm text-slate-500">Pembelian audio relaksasi (terpisah dari Pembayaran Reservasi).</p>

      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-pink-100 bg-white p-4">
        <label className="text-xs font-bold text-slate-600">Status
          <select data-testid="dt-filter-status" value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className="mt-1 block rounded-xl border border-pink-100 bg-[#FDFBFE] px-3 py-2 text-sm">
            {STATUSES.map((s) => <option key={s} value={s}>{s || "Semua"}</option>)}
          </select>
        </label>
        <label className="text-xs font-bold text-slate-600">Dari
          <input data-testid="dt-filter-start" type="date" value={start} onChange={(e) => { setPage(1); setStart(e.target.value); }} className="mt-1 block rounded-xl border border-pink-100 bg-[#FDFBFE] px-3 py-2 text-sm" />
        </label>
        <label className="text-xs font-bold text-slate-600">Sampai
          <input data-testid="dt-filter-end" type="date" value={end} onChange={(e) => { setPage(1); setEnd(e.target.value); }} className="mt-1 block rounded-xl border border-pink-100 bg-[#FDFBFE] px-3 py-2 text-sm" />
        </label>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-pink-100 bg-white">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-[#FAF7FF] text-left text-xs text-slate-500">
            <tr><th className="p-3">Order</th><th className="p-3">Pasien</th><th className="p-3">Item</th><th className="p-3">Nilai</th><th className="p-3">Status</th><th className="p-3">Waktu</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.order_no} data-testid={`dt-row-${o.order_no}`} className="border-t border-pink-50">
                <td className="p-3 font-mono text-xs">{o.order_no}</td>
                <td className="p-3">{o.patient_email}</td>
                <td className="p-3">{o.title} <span className="text-slate-400">({o.item_type})</span></td>
                <td className="p-3 font-bold">{rupiah(o.total)}</td>
                <td className="p-3">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${TONE[o.status] || "bg-slate-100 text-slate-600"}`}>{o.status}</span>
                  {o.needs_admin_review && <span className="ml-1 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-700">Partial Refund — Perlu Review</span>}
                </td>
                <td className="p-3 text-xs text-slate-500">{fmt(o.created_at)}</td>
                <td className="p-3 text-right">
                  <button data-testid={`dt-detail-${o.order_no}`} onClick={() => openDetail(o)} className="rounded-lg bg-slate-100 px-2.5 py-1.5 text-xs font-bold text-slate-600">Detail</button>
                  <button data-testid={`dt-sync-${o.order_no}`} onClick={() => sync(o.order_no)} disabled={syncing === o.order_no} className="ml-1 rounded-lg bg-[#EEE6FF] px-2.5 py-1.5 text-xs font-bold text-[#7C4DE0] disabled:opacity-50"><ArrowsClockwise size={13} className={syncing === o.order_no ? "inline animate-spin" : "inline"} /> Sync</button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">Tidak ada transaksi.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-500">{total} transaksi</span>
        <span className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-pink-200 px-3 py-1.5 font-bold text-[#7C4DE0] disabled:opacity-40">←</button>
          <span>Hal {page}/{pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-pink-200 px-3 py-1.5 font-bold text-[#7C4DE0] disabled:opacity-40">→</button>
        </span>
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={() => setDetail(null)}>
          <div data-testid="dt-detail-modal" onClick={(e) => e.stopPropagation()} className="my-6 w-full max-w-lg rounded-3xl bg-white p-6">
            <div className="mb-3 flex items-center justify-between"><h2 className="font-heading text-lg font-extrabold text-[#4C1D95]">Detail Transaksi</h2><button onClick={() => setDetail(null)}><X size={20} /></button></div>
            <div className="space-y-2 text-sm">
              <Row k="Order" v={detail.order_no} />
              <Row k="Pasien" v={detail.patient_email} />
              <Row k="Item" v={`${detail.title} (${detail.item_type})`} />
              <Row k="Subtotal" v={rupiah(detail.subtotal)} />
              <Row k="Total" v={rupiah(detail.total)} />
              <Row k="Status" v={detail.status + (detail.needs_admin_review ? " · Partial Refund — Perlu Review" : "")} />
              <Row k="Status Midtrans" v={(detail.payment || {}).transaction_status || detail.midtrans_status || "—"} />
              <Row k="Dibuat" v={fmt(detail.created_at)} />
              <Row k="Dibayar" v={fmt(detail.paid_at)} />
              <Row k="Refund" v={fmt(detail.refunded_at)} />
            </div>
            <button data-testid="dt-detail-sync" onClick={() => sync(detail.order_no)} disabled={syncing === detail.order_no} className="mt-4 w-full rounded-xl bg-[#7C4DE0] py-2.5 text-sm font-bold text-white disabled:opacity-60">Sinkronkan Status ke Midtrans</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between gap-4 border-b border-pink-50 py-1.5"><span className="text-slate-500">{k}</span><span className="text-right font-bold text-slate-800">{v}</span></div>;
}
